import { FC, useCallback, useContext, useEffect, useState } from "react";

import classNames from "classnames";

import { usePresence } from "../../hooks/usePresence";
import { DesignTokenProps, createComponentStyleHook } from "../../tokens";
import { Alert } from "../Alert";
import { AlertContext, AlertData } from "./AlertContext";

import legacyStyles from "./legacy/styles.module.scss";
import tokenStyles from "./styles.module.css";

/**
 * The alert container properties.
 */
export interface AlertContainerProps extends DesignTokenProps {
  /**
   * The current alert data.
   */
  readonly currentAlert: AlertData | null;

  /** Indicates whether the container belongs to a root alert container. */
  readonly root: boolean;
}

// must match the alertEnter/alertExit animation duration in styles.module.css and legacy/styles.module.scss
const ANIMATION_DURATION_MS = 200;

const useResolvedAlertContainerStyles = createComponentStyleHook<AlertContainerProps>({
  legacyStyles,
  tokenStyles,
  componentTokens: "alert",
});

/**
 * The alert container component.
 */
export const AlertContainer: FC<AlertContainerProps> = (props) => {
  const { currentAlert, root } = props;
  const [activeAlert, setActiveAlert] = useState<AlertData | null>(null);
  const { mode, styles, StyleModeBoundary } = useResolvedAlertContainerStyles(props);

  const presence = usePresence(activeAlert, ANIMATION_DURATION_MS);

  const { hideAlert } = useContext(AlertContext);

  useEffect(() => {
    setActiveAlert(currentAlert);

    const timer = setTimeout(() => {
      hideAlert();
    }, currentAlert?.timeout || 5000);

    return () => {
      clearTimeout(timer);
    };
  }, [currentAlert]);

  const dialogRefHandler = useCallback((ref: HTMLDivElement | null) => {
    if (ref) ref.showPopover();
  }, []);

  return (
    <StyleModeBoundary mode={mode}>
      {presence.map(({ item: alert, key, state, onAnimationEnd }) => (
        <div
          {...{ popover: "manual" }}
          key={key}
          ref={dialogRefHandler}
          className={classNames(styles.alertContainer, state)}
          data-testid={root ? "test__alert-container" : undefined}
          onAnimationEnd={onAnimationEnd}
        >
          <Alert message={alert.message} variant={alert.variant ?? alert.style ?? "banner"} type={alert.type} root={root} />
        </div>
      ))}
    </StyleModeBoundary>
  );
};
